if (!customElements.get('common-tab-products')) {
  class TabProducts extends HTMLElement {
    constructor() {
      super();
      this.classList.add('js-enabled');
      this.tabList = this.querySelector('.bfcm-tab-products__tab-list');
      this.productsStage = this.querySelector('.bfcm-tab-products__products-stage');
      this.mainImageStage = this.querySelector('.bfcm-tab-products__main-image-stage');
      this.containerList = this.productsStage?.querySelectorAll('.bfcm-tab-products__products-container') ?? [];
      this.tabSwiperContainer = this.querySelectorAll('.bfcm-tab-products__tab-wrapper');
      this.tabSwiperScrollbar = this.querySelector('.bfcm-tab-products__scrollbar');
      this.mainImageContainerList = this.mainImageStage?.querySelectorAll('.bfcm-tab-products__main-image-container') ?? [];
      this.tabSwiper = undefined;
      this.activeTabIndex = 0;
      this.tabIndicator = this.querySelector('.bfcm-tab-products__tab-indicator');
      this.tabWrapper = this.querySelector('.bfcm-tab-products__tab-wrapper');
      this.showMoreBtnList = this.querySelectorAll('.bfcm-tab-products__show-more-btn');
      this.initTabSwiper();
      this.initTabIndicator();
      this.initTabEvent();
      this.initShowMoreEvent();
      this.initMobileNavEvent();
    }

    initMobileNavEvent() {
      const mobileSections = this.querySelectorAll('.bfcm-tab-products__mobile-section');

      mobileSections.forEach((section) => {
        const nav = section.querySelector('.bfcm-tab-products__mobile-nav');
        const products = section.querySelector('.bfcm-tab-products__mobile-products');
        if (!nav || !products) return;

        const activeItem = nav.querySelector('.mobile-nav-item.active');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.updateMobileNavIndicator(nav, activeItem, false));
        });

        nav.addEventListener('click', (e) => {
          const target = e.target.closest('.mobile-nav-item');
          if (!target || !nav.contains(target)) return;

          this.activateMobileItem(section, Number(target.dataset.index));
        });

        nav.addEventListener('keydown', (e) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;

          const navItems = Array.from(nav.querySelectorAll('.mobile-nav-item'));
          if (!navItems.length) return;

          e.preventDefault();
          const currentIndex = navItems.findIndex((item) => item.classList.contains('active'));
          let nextIndex = currentIndex;

          if (e.key === 'ArrowLeft') nextIndex = Math.max(0, currentIndex - 1);
          if (e.key === 'ArrowRight') nextIndex = Math.min(navItems.length - 1, currentIndex + 1);
          if (e.key === 'Home') nextIndex = 0;
          if (e.key === 'End') nextIndex = navItems.length - 1;

          navItems[nextIndex].focus();
          this.activateMobileItem(section, nextIndex);
        });

        this.initMobileProductSwipe(section, products);
        window.addEventListener('resize', () => {
          this.updateMobileNavIndicator(nav, nav.querySelector('.mobile-nav-item.active'), false);
        });
      });
    }

    updateMobileNavIndicator(nav, activeItem, animate = true) {
      const indicator = nav?.querySelector('.mobile-nav-indicator');
      if (!indicator || !activeItem) return;

      const navRect = nav.getBoundingClientRect();
      const tabRect = activeItem.getBoundingClientRect();
      const left = tabRect.left - navRect.left + nav.scrollLeft;
      const top = tabRect.top - navRect.top + nav.scrollTop;

      if (!animate) indicator.style.transition = 'none';

      indicator.style.width = `${tabRect.width}px`;
      indicator.style.height = '1px';
      indicator.style.transform = `translate3d(${left}px, ${top + tabRect.height - 1}px, 0)`;

      if (!animate) {
        void indicator.offsetWidth;
        indicator.style.transition = '';
      }
    }

    switchMobileProduct(section, index) {
      const cards = Array.from(section.querySelectorAll('.mobile-product-card'));
      const currentCard = cards.find(
        (card) => card.classList.contains('active') && !card.classList.contains('is-leaving')
      );
      const nextCard = cards.find((card) => Number(card.dataset.index) === index);

      if (!nextCard || currentCard === nextCard) return;

      const transitionToken = Symbol('mobile-product-transition');
      currentCard?.classList.remove('is-entering');
      currentCard?.classList.add('is-leaving');
      currentCard?.setAttribute('aria-hidden', 'true');
      if (currentCard) currentCard._bfcmMobileTransitionToken = transitionToken;

      nextCard._bfcmMobileTransitionToken = undefined;
      nextCard.classList.add('active', 'is-entering');
      nextCard.removeAttribute('aria-hidden');
      void nextCard.offsetWidth;

      requestAnimationFrame(() => nextCard.classList.remove('is-entering'));

      window.setTimeout(() => {
        if (currentCard?._bfcmMobileTransitionToken === transitionToken) {
          currentCard.classList.remove('active', 'is-leaving');
          currentCard._bfcmMobileTransitionToken = undefined;
        }
      }, 440);
    }

    activateMobileItem(section, index) {
      const nav = section.querySelector('.bfcm-tab-products__mobile-nav');
      const navItems = Array.from(nav?.querySelectorAll('.mobile-nav-item') ?? []);
      const nextItem = navItems.find((item) => Number(item.dataset.index) === index);
      if (!nav || !nextItem) return;

      navItems.forEach((item) => {
        const isActive = item === nextItem;
        item.classList.toggle('active', isActive);
        item.setAttribute('aria-selected', String(isActive));
        item.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.updateMobileNavIndicator(nav, nextItem, true);
      nav.scrollTo({
        left: Math.max(0, nextItem.offsetLeft - (nav.clientWidth - nextItem.offsetWidth) / 2),
        behavior: 'smooth',
      });
      this.switchMobileProduct(section, index);
    }

    initMobileProductSwipe(section, products) {
      let startX = 0;
      let startY = 0;

      products.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 1) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });

      products.addEventListener('touchend', (e) => {
        if (!e.changedTouches.length) return;

        const deltaX = e.changedTouches[0].clientX - startX;
        const deltaY = e.changedTouches[0].clientY - startY;
        if (Math.abs(deltaX) < 50 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;

        const cards = Array.from(section.querySelectorAll('.mobile-product-card'));
        const currentIndex = cards.findIndex(
          (card) => card.classList.contains('active') && !card.classList.contains('is-leaving')
        );
        if (currentIndex < 0) return;

        const nextIndex = deltaX < 0
          ? Math.min(cards.length - 1, currentIndex + 1)
          : Math.max(0, currentIndex - 1);

        if (nextIndex !== currentIndex) this.activateMobileItem(section, nextIndex);
      }, { passive: true });
    }

    initTabSwiper() {
      this.tabSwiper = new Swiper(this.tabSwiperContainer, {
        slidesPerView: "auto",
        spaceBetween: 0,
        scrollbar: {
          el: this.tabSwiperScrollbar,
        },
        on: {
          transitionEnd: () => {
            const activeItem = this.tabList?.querySelector('.bfcm-tab-products__tab-item.active');
            if (activeItem) {
              this.updateTabIndicator(activeItem, false);
            }
          },
        },
      })
    }

    initTabIndicator() {
      if (!this.tabIndicator || !this.tabList || !this.tabWrapper) return;

      const syncIndicator = (animate = false) => {
        const activeItem = this.tabList.querySelector('.bfcm-tab-products__tab-item.active');
        if (activeItem) {
          this.updateTabIndicator(activeItem, animate);
        }
      };

      requestAnimationFrame(() => {
        requestAnimationFrame(() => syncIndicator(false));
      });

      window.addEventListener('resize', () => syncIndicator(false));

      if (typeof ResizeObserver !== 'undefined') {
        this._tabIndicatorObserver = new ResizeObserver(() => syncIndicator(false));
        this._tabIndicatorObserver.observe(this.tabWrapper);
        this.tabList.querySelectorAll('.bfcm-tab-products__tab-item').forEach((item) => {
          this._tabIndicatorObserver.observe(item);
        });
      }
    }

    updateTabIndicator(activeItem, animate = true) {
      if (!this.tabIndicator || !this.tabWrapper || !activeItem) return;

      const wrapperRect = this.tabWrapper.getBoundingClientRect();
      const tabRect = activeItem.getBoundingClientRect();
      const left = tabRect.left - wrapperRect.left;
      const top = tabRect.top - wrapperRect.top;

      if (!animate) {
        this.tabIndicator.style.transition = 'none';
      }

      this.tabIndicator.style.width = `${tabRect.width}px`;
      this.tabIndicator.style.height = '1px';
      this.tabIndicator.style.transform = `translate(${left}px, ${top + tabRect.height - 1}px)`;

      if (!animate) {
        void this.tabIndicator.offsetWidth;
        this.tabIndicator.style.transition = '';
      }
    }

    switchPanelGroup(stage, panels, tab, direction) {
      if (!stage || !panels.length) return;

      const currentPanel = Array.from(panels).find(
        (panel) => panel.classList.contains('active') && !panel.classList.contains('is-leaving')
      );
      const nextPanel = Array.from(panels).find((panel) => panel.getAttribute('data-tab') === tab);

      if (!nextPanel || currentPanel === nextPanel) return;

      const transitionToken = Symbol('panel-transition');
      const shouldAnimateHeight = stage === this.productsStage;
      const currentHeight = currentPanel?.offsetHeight ?? stage.offsetHeight;

      if (shouldAnimateHeight) {
        stage._bfcmHeightTransitionToken = transitionToken;
        stage.style.height = `${currentHeight}px`;
        void stage.offsetHeight;
      }

      stage.dataset.direction = direction;
      currentPanel?.classList.remove('is-entering');
      currentPanel?.classList.add('is-leaving');
      currentPanel?.setAttribute('aria-hidden', 'true');
      if (currentPanel) currentPanel._bfcmTransitionToken = transitionToken;

      nextPanel._bfcmTransitionToken = undefined;
      nextPanel.classList.add('active', 'is-entering');
      nextPanel.removeAttribute('aria-hidden');
      void nextPanel.offsetWidth;

      requestAnimationFrame(() => {
        nextPanel.classList.remove('is-entering');
        if (shouldAnimateHeight) stage.style.height = `${nextPanel.offsetHeight}px`;
      });

      window.setTimeout(() => {
        if (currentPanel?._bfcmTransitionToken === transitionToken) {
          currentPanel.classList.remove('active', 'is-leaving');
          currentPanel._bfcmTransitionToken = undefined;
        }
        if (shouldAnimateHeight && stage._bfcmHeightTransitionToken === transitionToken) {
          stage.style.height = '';
          stage._bfcmHeightTransitionToken = undefined;
        }
      }, 500);
    }

    activateTab(target) {
      if (!target || target.classList.contains('active')) return;

      const tab = target.getAttribute('data-tab');
      const nextIndex = Number(target.getAttribute('data-index')) - 1;
      const direction = nextIndex >= this.activeTabIndex ? 'forward' : 'backward';

      this.tabList.querySelectorAll('.bfcm-tab-products__tab-item').forEach((item) => {
        const isActive = item === target;
        item.classList.toggle('active', isActive);
        item.setAttribute('aria-selected', String(isActive));
        item.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.updateTabIndicator(target, true);
      this.tabSwiper?.slideTo(nextIndex);
      this.switchPanelGroup(this.mainImageStage, this.mainImageContainerList, tab, direction);
      this.switchPanelGroup(this.productsStage, this.containerList, tab, direction);
      this.activeTabIndex = nextIndex;
    }

    initTabEvent() {
      if (!this.tabList) return;

      this.tabList.addEventListener('click', (e) => {
        const target = e.target.closest('.bfcm-tab-products__tab-item');
        if (!target || !this.tabList.contains(target)) return;

        this.activateTab(target);
      });

      this.tabList.addEventListener('keydown', (e) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;

        const tabs = Array.from(this.tabList.querySelectorAll('.bfcm-tab-products__tab-item'));
        if (!tabs.length) return;

        e.preventDefault();
        let nextIndex = this.activeTabIndex;

        if (e.key === 'ArrowLeft') nextIndex = Math.max(0, this.activeTabIndex - 1);
        if (e.key === 'ArrowRight') nextIndex = Math.min(tabs.length - 1, this.activeTabIndex + 1);
        if (e.key === 'Home') nextIndex = 0;
        if (e.key === 'End') nextIndex = tabs.length - 1;

        tabs[nextIndex].focus();
        this.activateTab(tabs[nextIndex]);
      });
    }

    initShowMoreEvent() {
      const item = this.querySelector('.card-product-4');
      const itemHeight = item.offsetHeight;
      this.querySelectorAll('.bfcm-tab-products__products-container-inner .swiper-wrapper').forEach((e) => {
        if (e.classList.contains('need-show-more')) {
          const columnsDesktop = 4;
          const items = Array.from(e.children);
          const itemNum = e.childElementCount;
          const rowNum = Math.ceil(itemNum / columnsDesktop);
          const parent = e.parentElement;
          const showMoreBtn = parent.querySelector('.bfcm-tab-products__show-more-btn');

          const itemBoxNormal = document.createElement('div');
          const itemBoxExpand = document.createElement('div');
          e.innerHTML = '';
          itemBoxNormal.className = 'normal-box';
          itemBoxNormal.style.setProperty('grid-template-rows', `repeat(2, 1fr)`);

          itemBoxExpand.className = 'expand-box';
          itemBoxExpand.style.setProperty('grid-template-rows', `repeat(${rowNum - 2}, 1fr)`);

          let itemBoxExpandNum = 0;
          items.forEach(el => {
            if (el.classList.contains('large-card') || el.classList.contains('medium-card')) {
              itemBoxExpandNum += 1;
            }
          })

          // 再加3个small-card
          if (itemBoxExpandNum === 0) {
            itemBoxExpandNum = 6;
          } else {
            itemBoxExpandNum += 3;
          }

          // 分配元素到两个容器
          items.forEach((item, index) => {
            if (index < itemBoxExpandNum) {
              itemBoxNormal.appendChild(item);
            } else {
              itemBoxExpand.appendChild(item);
            }
          });
          e.appendChild(itemBoxNormal);
          e.appendChild(itemBoxExpand);

          if (itemNum <= itemBoxExpandNum) {
            showMoreBtn.style.setProperty('display', 'none');
            itemBoxNormal.style.setProperty('grid-template-rows', `repeat(${rowNum}, 1fr)`);
          }

          showMoreBtn.addEventListener('click', () => {
            e.classList.toggle('collapsed');
            e.classList.toggle('expanded');
          });
          e.classList.add('show-more-ready');
        }
      });
    }
  }

  customElements.define('common-tab-products', TabProducts);
}


if (!customElements.get('bfcm-tab-products-swiper')) {
  class BfcmTabProductsSwiper extends HTMLElement {
    constructor() {
      super();
      this._swiperInstance = null;
      this.columnsDesktop = Number(this.getAttribute("data-columns-desktop") ?? 2);
      this._swiperContainer = this;

      const isMobile = `(max-width: 768px)`;
      const isMobileMQL = window.matchMedia(isMobile);

      isMobileMQL.addEventListener('change', (event) => {
        if (!event.matches) {
          this.createSlider();
        } else if (this.mySwiper) {
          this.destroySwiper();
        }
      });
      if (!isMobileMQL.matches) this.createSlider();
    }

    createSlider() {
      const nextButton = this.querySelector('.swiper-btn-next');
      const prevButton = this.querySelector('.swiper-btn-prev');
      const pagination = this.querySelector(`.swiper-pagination`);

      this._swiperInstance = new Swiper(this._swiperContainer, {
        slidesPerView: 3,
        slidesPerGroup: 1,
        spaceBetween: 20,
        loop: false,
        direction: "horizontal",
        navigation: {
          nextEl: nextButton,
          prevEl: prevButton,
        },
        pagination: {
          el: pagination,
          clickable: true,
        },
        breakpoints: {
          769: {
            slidesPerView: 2,
            spaceBetween: 20,
          },
          990: {
            slidesPerView: 2,
            spaceBetween: 20,
          },
          1200: {
            slidesPerView: 2.5,
            spaceBetween: 20,
          },
        }
      });
    }

    destroySwiper() {
      this._swiperInstance.destroy();
    }
  }

  customElements.define('bfcm-tab-products-swiper', BfcmTabProductsSwiper);
}
